import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { Editor } from '@tiptap/core';
import MentionList, { MentionListHandle } from './mentionList';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';

// Define the type of the render function
interface MentionRenderProps {
  editor: Editor;
  query: string;
  range: { from: number; to: number };
  clientRect?: (() => DOMRect | null) | null; // Match Tiptap's exact signature
}

// Define mention user structure
interface MentionUser {
  name?: string;
}

interface SuggestionConfig {
  items: (props: { query: string }) => MentionUser[];
  render: () => {
    onStart: (props: MentionRenderProps) => void;
    onUpdate: (props: MentionRenderProps) => void;
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
    onExit: () => void;
  };
}

const suggestion: SuggestionConfig = {
  items: ({ query }: { query: string }): MentionUser[] => {
    const mentionUsers = UserPreferenceSingleton.getInstance().getMentionUsers() as MentionUser[] | undefined;
    return (
      mentionUsers?.filter((item) => {
        const words = item?.name?.split(' ').map((word: string) => word.toLowerCase()) || [];
        return words.some((word: string) => word.startsWith(query.toLowerCase()));
      }) || []
    );
  },

  render: () => {
    let component: ReactRenderer<MentionListHandle> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: MentionRenderProps) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component?.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'teamcamp',
          arrow: false
        });
      },

      onUpdate(props: MentionRenderProps) {
        component?.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup?.[0].setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect
        });
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === 'Escape') {
          popup?.[0].hide();
          return true;
        }

        return component?.ref?.onKeyDown?.(props) || false;
      },

      onExit() {
        popup?.[0].destroy();
        component?.destroy();
      }
    };
  }
};

export default suggestion;
